import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Button } from "react-bootstrap";

import DeleteModal from "../../../../components/DeleteModal";
import { isJeepRallyAdmin } from "../../Categories/utils/category.utils";
import { useGetAllRoutes, useDeleteRouteMutation } from "../../../../../api/jeep-rally/routes/routes.hooks";
import { normalizeRoutes } from "../utils/route.utils";
import RoutesTable from "./_components/RoutesTable";

const RoutesListPage = () => {
  const history = useHistory();
  const canManage = isJeepRallyAdmin();

  const {
    data,
    isLoading,
    isError,
    error,
  } = useGetAllRoutes({ enabled: true });

  const routes = useMemo(() => normalizeRoutes(data), [data]);

  const {
    mutate: deleteRoute,
    isLoading: isDeleting,
    isPending: isDeletePending,
  } = useDeleteRouteMutation();
  const deleting = isDeleting || isDeletePending;

  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setSelectedRoute(null);
  };

  const openDeleteModal = (route) => {
    setSelectedRoute(route);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    const routeId = selectedRoute?._id || selectedRoute?.id;
    if (!routeId || !canManage) return;

    deleteRoute(
      { routeId },
      {
        onSuccess: () => closeDeleteModal(),
      }
    );
  };

  const handleEdit = (route) => {
    const routeId = route?._id || route?.id;
    if (!routeId) return;
    history.push(`/jeep-rally/routes/edit/${routeId}`, { route });
  };

  const handleView = (route) => {
    const routeId = route?._id || route?.id;
    if (!routeId) return;
    history.push(`/jeep-rally/routes/view/${routeId}`, { route });
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border" role="status" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load routes.{" "}
            {error?.response?.data?.message || ""}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0">Routes ({routes.length})</h4>
          <Button
            type="button"
            onClick={() => history.push("/jeep-rally/routes/create")}
            disabled={!canManage}
          >
            + Create Route
          </Button>
        </div>

        <div className="card-body">
          {routes.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No routes found</p>
            </div>
          ) : (
            <RoutesTable
              routes={routes}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={openDeleteModal}
              isDeleting={deleting}
              canManage={canManage}
            />
          )}

          {!canManage && (
            <Alert variant="warning" className="mt-3 mb-0">
              Admin access is required to create/edit/delete routes.
            </Alert>
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Route"
        message={`Are you sure you want to delete ${
          selectedRoute?.title || "this route"
        }?`}
        confirmText="Delete Route"
        isLoading={deleting}
      />
    </div>
  );
};

export default RoutesListPage;

